// Role utility functions for common access patterns

import { PROTECTED_ROUTES, PUBLIC_ROUTES } from '@/constants/routes'

export type UserRole = 'hr_admin' | 'general_manager' | 'branch_manager' | 'system_admin' | 'unassigned'

export interface RoleRequirement {
  role?: UserRole
  anyOf?: UserRole[]
  allOf?: UserRole[]
  customCheck?: (userRole: UserRole) => boolean
}

// Common role requirement presets
export const RoleRequirements = {
  // Single role requirements
  systemAdminOnly: { role: 'system_admin' as UserRole },
  hrAdminOnly: { role: 'hr_admin' as UserRole },
  generalManagerOnly: { role: 'general_manager' as UserRole },

  // OR logic - user needs ANY of these roles
  adminRoles: { anyOf: ['system_admin', 'hr_admin'] as UserRole[] },
  managerRoles: { anyOf: ['system_admin', 'general_manager', 'branch_manager'] as UserRole[] },
  allRoles: { anyOf: ['system_admin', 'hr_admin', 'general_manager', 'branch_manager'] as UserRole[] },

  // Custom checks for specific scenarios
  systemAdminOrHrAdmin: { anyOf: ['system_admin', 'hr_admin'] as UserRole[] },
  
  // Example of custom logic
  canManageUsers: {
    customCheck: (userRole: UserRole) => {
      return userRole === 'system_admin' || userRole === 'hr_admin'
    }
  },

  canViewReports: {
    anyOf: ['system_admin', 'hr_admin', 'general_manager', 'branch_manager'] as UserRole[]
  },

  canModifySystemSettings: {
    role: 'system_admin' as UserRole
  }
}

// Helper function to check if a user role satisfies a requirement
export const checkRoleRequirement = (
  userRole: UserRole | undefined, 
  requirement: RoleRequirement
): boolean => {
  if (!userRole) return false

  // Check single role requirement
  if (requirement.role) {
    return userRole === requirement.role
  }

  // Check ANY OF roles (OR logic)
  if (requirement.anyOf) {
    return requirement.anyOf.includes(userRole)
  }

  // Check ALL OF roles (AND logic)
  // Note: This assumes single role per user. Modify if users can have multiple roles
  if (requirement.allOf) {
    return requirement.allOf.includes(userRole)
  }

  // Custom role check function
  if (requirement.customCheck) {
    return requirement.customCheck(userRole)
  }

  return true
}

// Helper to get role hierarchy level (higher number = more permissions)
export const getRoleLevel = (role: UserRole): number => {
  const levels: Record<UserRole, number> = {
    'system_admin': 4,
    'hr_admin': 3,
    'general_manager': 2,
    'branch_manager': 1,
    'unassigned': 0
  }
  return levels[role] || 0
}

// Check if user role has equal or higher permissions than required role
export const hasEqualOrHigherRole = (userRole: UserRole, requiredRole: UserRole): boolean => {
  return getRoleLevel(userRole) >= getRoleLevel(requiredRole)
}

// Role requirement for "at least" a certain role level
export const atLeastRole = (minRole: UserRole): RoleRequirement => ({
  customCheck: (userRole: UserRole) => hasEqualOrHigherRole(userRole, minRole)
})

// Get the default landing page for a user role
export const getDefaultRedirectPath = (userRole: UserRole): string => {
  const defaultPaths: Record<UserRole, string> = {
    'hr_admin': PROTECTED_ROUTES.HR,
    'general_manager': PROTECTED_ROUTES.OPS, // General managers can access ops agent
    'branch_manager': PROTECTED_ROUTES.OPS, // Branch managers go to ops agent for their store
    'system_admin': PROTECTED_ROUTES.SETTINGS, // System admins go to settings
    'unassigned': PUBLIC_ROUTES.PENDING // Redirect unassigned users to pending approval page
  }

  return defaultPaths[userRole] || PROTECTED_ROUTES.HR // fallback to hr page
}

// Check if a user can access the root path "/"
export const canAccessRootPath = (userRole: UserRole): boolean => {
  // Since we want role-based redirection, users shouldn't access "/"
  // They should be redirected to their specific landing page
  return false
} 